// Small components for surfacing assignments throughout the student app.
//   • AssignmentsHeaderPill — header chip showing pending/overdue count.
//   • TopicAssignmentsPanel — bottom-of-topic-page list for a topic.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './studentApi';
import { toast } from 'sonner';
import { ClipboardList, AlertCircle, Check, CalendarDays, Clock } from 'lucide-react';
import { notifyError } from '../../lib/errors';

const POLL_MS = 120_000;

export function AssignmentsHeaderPill() {
  const nav = useNavigate();
  const [counts, setCounts] = useState({ pending: 0, overdue: 0 });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get('/student/assignments/pending-count');
        if (!cancelled) setCounts({ pending: r?.pending || 0, overdue: r?.overdue || 0 });
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, POLL_MS);
    // Refresh immediately whenever the student switches institutions
    // (StudentApp dispatches this event after a successful switch).
    const onSwitch = () => load();
    window.addEventListener('desky:tenant-switched', onSwitch);
    // Refresh immediately when the student marks an assignment done/pending
    // anywhere in the app (assignments page, topic panel) — the pill must
    // only ever show what is still outstanding, never a stale total.
    window.addEventListener('desky:assignments-changed', onSwitch);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('desky:tenant-switched', onSwitch);
      window.removeEventListener('desky:assignments-changed', onSwitch);
    };
  }, []);

  if (!counts.pending) return null;
  const danger = counts.overdue > 0;
  return (
    <button onClick={() => nav('/student/assignments')}
      data-testid="assignments-header-pill"
      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition-all hover:-translate-y-0.5"
      style={{
        borderColor: danger ? '#dc2626' : 'var(--st-primary)',
        background: danger ? 'rgba(220,38,38,0.08)' : 'color-mix(in srgb, var(--st-primary) 8%, transparent)',
        color: danger ? '#dc2626' : 'var(--st-primary)',
      }}>
      {danger ? <AlertCircle className="w-3.5 h-3.5" /> : <ClipboardList className="w-3.5 h-3.5" />}
      <span>{counts.pending}</span>
      {danger && <span className="text-[9px] font-extrabold uppercase tracking-wider">{counts.overdue} late</span>}
    </button>
  );
}

export function TopicAssignmentsPanel({ topicId }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/student/assignments/by-topic/${topicId}`);
      setItems(r.assignments || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [topicId]);

  const toggleDone = async (a) => {
    const currentlyDone = a.status === 'done';
    setActing(a.id);
    try {
      await api.post(`/student/assignments/${a.id}/${currentlyDone ? 'mark-pending' : 'mark-done'}`);
      toast.success(currentlyDone ? 'Marked as pending' : 'Marked done!');
      await load();
      // Tell the header pill to re-count right away.
      try { window.dispatchEvent(new CustomEvent('desky:assignments-changed')); } catch { /* noop */ }
    } catch (e) { notifyError(e, 'Could not update'); }
    finally { setActing(null); }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section className="rounded-3xl border-2 overflow-hidden mt-6"
      style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
      data-testid="topic-assignments-panel">
      <div className="px-5 py-3 flex items-center gap-3 border-b"
        style={{ borderColor: 'var(--st-border)', background: 'color-mix(in srgb, var(--st-primary) 6%, transparent)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
          style={{ background: 'var(--st-primary)' }}>
          <ClipboardList className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest opacity-60">Homework for this topic</div>
          <div className="text-base font-extrabold">{items.length} assignment{items.length === 1 ? '' : 's'}</div>
        </div>
        <button onClick={() => nav('/student/assignments')}
          data-testid="topic-asgn-view-all"
          className="text-[11px] font-bold opacity-70 hover:opacity-100">
          View all →
        </button>
      </div>

      <div className="divide-y" style={{ borderColor: 'var(--st-border)' }}>
        {items.map(a => {
          const done = a.status === 'done';
          const due = a.due_at ? new Date(a.due_at) : null;
          return (
            <div key={a.id} className="px-5 py-4 flex items-start gap-3"
              data-testid={`topic-asgn-${a.id}`}
              style={done ? { opacity: 0.6 } : {}}>
              <button onClick={() => toggleDone(a)} disabled={acting === a.id}
                data-testid={`topic-asgn-toggle-${a.id}`}
                className="w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
                style={{
                  borderColor: done ? 'var(--st-primary)' : (a.overdue ? '#dc2626' : 'var(--st-border)'),
                  background: done ? 'var(--st-primary)' : 'transparent',
                  color: done ? 'white' : 'transparent',
                }}>
                {done && <Check className="w-3.5 h-3.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className={`text-sm font-bold ${done ? 'line-through' : ''}`}>{a.title}</h4>
                  {a.overdue && !done && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider text-white"
                      style={{ background: '#dc2626' }}>
                      <AlertCircle className="w-2.5 h-2.5" /> Overdue
                    </span>
                  )}
                </div>
                {a.description && (
                  <p className="text-[12px] opacity-70 mt-1 line-clamp-2">{a.description}</p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] opacity-70">
                  {due && (
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />
                      {due.toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                  {a.due_time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.due_time}</span>}
                  <span className="font-bold" style={{ color: 'var(--st-primary)' }}>+{a.points} pts</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
